import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InfoIcon from "@mui/icons-material/Info";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import ExportHandler from "./exportHandler";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { getDashboardData } from "../../../redux/actions/dashboardActionCreators";
import { useDispatch, useSelector } from "react-redux";

export default function Page1() {
  // const lang = localStorage.getItem("i18nextLng");

  // const orgaId = useSelector((state) => state.organizationProfile.id);
  const dashboardData = useSelector((state) => state.dashboard.jsonData);
  // const dashboardStatus = useSelector((state) => state.dashboard.status);
  // const dashboardMessage = useSelector((state) => state.dashboard.message);
  // const dashboardMessageFr = useSelector((state) => state.dashboard.message_fr);

  // const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const [selectedThreat, setSelectedThreat] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Safely access data with fallbacks
  const tefData = dashboardData?.tef_values || [];
  const aleData = dashboardData?.ale_values || [];
  const tlmData = dashboardData?.tlm_values || [];
  const orgaDiscription =
    dashboardData?.orga_description || "No description available";

  // Calculate sums only when data is available
  const tefAvgSum = tefData.reduce((sum, item) => sum + (item?.avg || 0), 0);
  const aleAvgSum = aleData.reduce(
    (sum, item) => sum + (item?.ale_mean || 0),
    0
  );
  const tlmAvgSum = tlmData.reduce((sum, item) => sum + (item?.median || 0), 0);

  const tlmDisplayValue = selectedThreat
    ? tlmData.find((item) => item.threat === selectedThreat)?.median || 0
    : tlmAvgSum;
  const tefDisplayValue = selectedThreat
    ? tefData.find((item) => item.threat === selectedThreat)?.avg || 0
    : tefAvgSum;
  const aleDisplayValue = selectedThreat
    ? aleData.find((item) => item.threat === selectedThreat)?.ale_mean || 0
    : aleAvgSum;

  const handleChange = (event) => setSelectedThreat(event.target.value);
  const handleInfoClick = (path) => history.push(path);

  const handleExportToPDF = async () => {
    try {
      setIsExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 500)); // Reduced delay

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      const addNewPage = () => {
        pdf.addPage("a4", "p");
        yPos = margin;
      };

      // Process all elements in order within each threat container
      const processContentElements = async (container) => {
        // Get all content elements in their original order
        const contentElements = container.querySelectorAll(
          "#text-section, #graph-section, #table-section"
        );

        for (const element of contentElements) {
          // Skip hidden elements
          if (window.getComputedStyle(element).display === "none") continue;

          if (element.id === "text-section") {
            await processTextElement(element);
          } else if (element.id === "graph-section") {
            await processGraphElement(element);
          }
          // Add other content types as needed
        }
      };

      const processTextElement = async (element) => {
        pdf.setFont("helvetica");
        pdf.setFontSize(10);

        const text = element.innerText;
        const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
        const lineHeight = 7;
        const estimatedHeight = lines.length * lineHeight;

        if (yPos + estimatedHeight > pageHeight - margin) {
          addNewPage();
        }

        pdf.text(lines, margin, yPos);
        yPos += estimatedHeight + 10;
      };

      const processGraphElement = async (element) => {
        // Temporarily hide other elements to ensure clean capture
        const siblings = Array.from(element.parentElement.children).filter(
          (child) => child !== element
        );

        const originalDisplay = siblings.map((el) => el.style.display);
        siblings.forEach((el) => (el.style.display = "none"));

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#f9f9f9", // Match your background
        });

        // Restore original display
        siblings.forEach((el, i) => {
          el.style.display = originalDisplay[i];
        });

        const imgData = canvas.toDataURL("image/png", 1.0); // PNG for better quality
        const imgProps = pdf.getImageProperties(imgData);
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        if (yPos + imgHeight > pageHeight - margin) {
          addNewPage();
        }

        pdf.addImage(imgData, "PNG", margin, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 15;
      };

      // Process all threat containers in order
      const threatContainers = Array.from(
        document.querySelectorAll('[id^="tlm-threat-"]')
      ).sort((a, b) => {
        // Ensure numerical order (tlm-threat-0, tlm-threat-1, etc.)
        const aNum = parseInt(a.id.split("-").pop());
        const bNum = parseInt(b.id.split("-").pop());
        return aNum - bNum;
      });

      for (const container of threatContainers) {
        await processContentElements(container);

        // Add space between containers if room remains
        if (yPos < pageHeight - 50) {
          yPos += 20;
        } else {
          addNewPage();
        }
      }

      // Remove last empty page if needed
      const pageCount = pdf.internal.getNumberOfPages();
      if (pageCount > 1 && yPos === margin) {
        pdf.deletePage(pageCount);
      }

      pdf.save("risk-report.pdf");
    } catch (error) {
      console.error("PDF Export Error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // const handleExportToPDF = async () => {
  //   try {
  //     setIsExporting(true);
  //     await new Promise((resolve) => setTimeout(resolve, 1200));

  //     const pdf = new jsPDF("p", "mm", "a4");

  //     const tlmThreats = Array.from(
  //       document.querySelectorAll('[id^="tlm-threat-"]')
  //     );
  //     const tefThreats = Array.from(
  //       document.querySelectorAll('[id^="tef-threat-"]')
  //     );
  //     const aleThreats = Array.from(
  //       document.querySelectorAll('[id^="ale-threat-"]')
  //     );

  //     const allThreats = [...tefThreats, ...aleThreats, ...tlmThreats];

  //     for (let i = 0; i < allThreats.length; i++) {
  //       const threatElement = allThreats[i];
  //       if (!threatElement) continue;

  //       const isTlmThreat =
  //         threatElement.id.startsWith("tlm-threat-") ||
  //         threatElement.id.startsWith("tef-threat-") ||
  //         threatElement.id.startsWith("ale-threat-");

  //       // Capture the element as an image
  //       const canvas = await html2canvas(threatElement, {
  //         scale: 1.5,
  //         useCORS: true,
  //       });
  //       const imgData = canvas.toDataURL("image/jpeg", 0.8);

  //       const pdfWidth = pdf.internal.pageSize.getWidth();
  //       const imgProps = pdf.getImageProperties(imgData);
  //       const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
  //       const requiredHeight = imgHeight + 20;

  //       if (i === 0) {
  //         if (
  //           isTlmThreat &&
  //           requiredHeight > pdf.internal.pageSize.getHeight()
  //         ) {
  //           pdf.deletePage(1);
  //           pdf.addPage([pdfWidth, requiredHeight]);
  //         }
  //       } else {
  //         if (isTlmThreat) {
  //           pdf.addPage([pdfWidth, requiredHeight]);
  //         } else {
  //           pdf.addPage();
  //         }
  //       }

  //       pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
  //     }

  //     pdf.save("exported-threats.pdf");
  //   } catch (error) {
  //     console.error("Error exporting PDF:", error);
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };

  // const handleExportToPDF = async () => {
  //   try {
  //     setIsExporting(true);
  //     await new Promise((resolve) => setTimeout(resolve, 1200)); // Same delay as PDF version

  //     // Get all threat elements (identical to PDF version)
  //     const tlmThreats = Array.from(
  //       document.querySelectorAll('[id^="tlm-threat-"]')
  //     );
  //     const tefThreats = Array.from(
  //       document.querySelectorAll('[id^="tef-threat-"]')
  //     );
  //     const aleThreats = Array.from(
  //       document.querySelectorAll('[id^="ale-threat-"]')
  //     );
  //     const allThreats = [...tefThreats, ...aleThreats, ...tlmThreats];

  //     // Create HTML structure
  //     let htmlContent = `<!DOCTYPE html>
  // <html lang="en">
  // <head>
  //     <meta charset="UTF-8">
  //     <meta name="viewport" content="width=device-width, initial-scale=1.0">
  //     <title>Exported Threats</title>
  //     <style>
  //         body {
  //             margin: 0;
  //             padding: 20px;
  //             background-color: #f5f5f5;
  //         }
  //         .threat-image {
  //             width: 100%;
  //             max-width: 1000px;
  //             margin: 0 auto 40px auto;
  //             display: block;
  //             box-shadow: 0 0 10px rgba(0,0,0,0.1);
  //         }
  //         .page-break {
  //             page-break-after: always;
  //             height: 0;
  //             visibility: hidden;
  //         }
  //         .export-header {
  //             text-align: center;
  //             margin-bottom: 30px;
  //         }
  //     </style>
  // </head>
  // <body>
  //     <div class="export-header">
  //         <h1>Threat Model Export</h1>
  //         <p>Generated on ${new Date().toLocaleString()}</p>
  //     </div>
  // `;

  //     // Process each threat (same logic as PDF version)
  //     for (let i = 0; i < allThreats.length; i++) {
  //       const threatElement = allThreats[i];
  //       if (!threatElement) continue;

  //       const isTlmThreat =
  //         threatElement.id.startsWith("tlm-threat-") ||
  //         threatElement.id.startsWith("tef-threat-") ||
  //         threatElement.id.startsWith("ale-threat-");

  //       // Capture the element as an image (same settings as PDF version)
  //       const canvas = await html2canvas(threatElement, {
  //         scale: 1.5,
  //         useCORS: true,
  //       });
  //       const imgData = canvas.toDataURL("image/jpeg", 0.8);

  //       // Add to HTML
  //       htmlContent += `
  //         <img src="${imgData}" class="threat-image" alt="${threatElement.id}">
  //       `;

  //       // Add page breaks similar to PDF version
  //       if (i < allThreats.length - 1) {
  //         if (isTlmThreat) {
  //           // For TLM threats, add more space (mimicking PDF height adjustment)
  //           htmlContent += `<div style="height: 20px;"></div>`;
  //         } else {
  //           htmlContent += `<div class="page-break"></div>`;
  //         }
  //       }
  //     }

  //     htmlContent += `
  // </body>
  // </html>`;

  //     // Create download link
  //     const blob = new Blob([htmlContent], { type: "text/html" });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = "exported-threats.html";
  //     document.body.appendChild(a);
  //     a.click();

  //     // Clean up
  //     setTimeout(() => {
  //       document.body.removeChild(a);
  //       URL.revokeObjectURL(url);
  //       setIsExporting(false);
  //     }, 100);
  //   } catch (error) {
  //     console.error("Error exporting HTML:", error);
  //     setIsExporting(false);
  //   }
  // };

  return (
    <>
      <Box sx={{ marginBottom: { xs: "25%" } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Button
            onClick={() => handleInfoClick("/risk_profile/heatmap")}
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<ScatterPlotIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              py: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                transform: "translateY(-2px)",
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            Heatmap
          </Button>

          <Button
            onClick={() => handleInfoClick("/risk_profile/discovered_assets")}
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<AssessmentIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              py: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                transform: "translateY(-2px)",
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            Discovered Assets
          </Button>

          <Button
            onClick={() => handleInfoClick("/risk_profile/weakness")}
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<GppMaybeIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              py: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                transform: "translateY(-2px)",
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            Top 10 Weaknesses
          </Button>

          <Button
            onClick={handleExportToPDF}
            sx={{
              marginLeft: { sm: "60px" },
              minWidth: { xs: "100%", sm: 200 },
              py: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                transform: "translateY(-2px)",
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            {isExporting ? (
              <CircularProgress color="success" size="30px" />
            ) : (
              "Export"
            )}
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "stretch",
            maxWidth: "100%",
            margin: "auto",
            mt: { xs: 2, sm: 2 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: { xs: 2, sm: 2 },
              flex: 1,
              width: "100%",
            }}
          >
            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(25% - 32px)",
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
              }}
            >
              <FormControl fullWidth>
                <InputLabel
                  id="threat-select-label"
                  sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
                >
                  Threat Type
                </InputLabel>
                <Select
                  labelId="threat-select-label"
                  id="threat-select"
                  value={selectedThreat}
                  label="Threat Type"
                  onChange={handleChange}
                  sx={{
                    height: { xs: "52px", sm: "64px" },
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
                  >
                    <em>All threats</em>
                  </MenuItem>
                  {tefData.map((threat, index) => (
                    <MenuItem
                      key={index}
                      value={threat?.threat}
                      sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
                    >
                      {t(threat?.threat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(25% - 32px)",
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <IconButton
                aria-label="info"
                size="small"
                onClick={() => handleInfoClick("/risk_profile/proba")}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  color: "text.secondary",
                }}
              >
                <InfoIcon fontSize="large" color="success" />
              </IconButton>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Probability Of Successful Attack
              </Typography>
            </Box>

            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(25% - 32px)",
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <IconButton
                aria-label="info"
                size="small"
                onClick={() => handleInfoClick("/risk_profile/tef")}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  color: "text.secondary",
                }}
              >
                <InfoIcon fontSize="large" color="success" />
              </IconButton>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} TEF Average`
                  : "TEF Average Sum"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {tefDisplayValue.toFixed(2)}
              </Typography>
            </Box>

            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(25% - 32px)",
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <IconButton
                aria-label="info"
                size="small"
                onClick={() => handleInfoClick("/risk_profile/ale")}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  color: "text.secondary",
                }}
              >
                <InfoIcon fontSize="large" color="success" />
              </IconButton>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} ALE Median`
                  : "ALE Median Sum"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {(aleDisplayValue / 1000).toFixed(0)} M$
              </Typography>
            </Box>

            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(25% - 32px)",
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <IconButton
                aria-label="info"
                size="small"
                onClick={() => handleInfoClick("/risk_profile/tlm")}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  color: "text.secondary",
                }}
              >
                <InfoIcon fontSize="large" color="success" />
              </IconButton>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} TLM Median`
                  : "TLM Median Sum"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {(tlmDisplayValue / 1000).toFixed(0)} M$
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 2, sm: 4 },
            mt: 4,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              bgcolor: "background.paper",
              width: { xs: "90%", sm: "97%", md: "97%" },
              minHeight: "350px",
              height: "auto",
              textAlign: "center",
            }}
          >
            <Typography variant={"h4"} sx={{ marginBottom: "8px" }}>
              Organisation Description
            </Typography>
            <Typography
              sx={{
                textAlign: "start",
              }}
            >
              {orgaDiscription}
            </Typography>
          </Box>
        </Box>

        {isExporting && (
          <Box
            id="export-content"
            sx={{
              padding: "20px",
              position: "absolute",
              left: "-9999px",
              top: "-9999px",
            }}
          >
            <ExportHandler />
          </Box>
        )}
      </Box>
    </>
  );
}
